import React, { useState } from 'react';
import { useUserCollaboration } from '../hooks/useUserCollaboration';
import { toast } from 'sonner';
import { Mail } from 'lucide-react';
import { Badge } from './ui/badge';
import UserRolesForm from '../pages/overlays/UserRolesForm';
import { SortableList, DragHandleSpacer } from './ui/sortable';
import { cn } from '../lib/utils';

// shadcn UI components
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

/**
 * Fixed column template, shared by the header and every row. Columns never size
 * to their content, so a value getting longer while it's edited can't push its
 * neighbours around. The list scrolls sideways below this width rather than
 * squeezing columns.
 */
const GRID = 'grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1.1fr)_4rem] gap-x-2 items-center';
const MIN_WIDTH = 'min-w-[760px]';

/**
 * One cell box for both states. The display and the input are the same height,
 * padding and border width — only the border colour changes — so entering edit
 * mode changes nothing about the row's size. Like a spreadsheet cell.
 */
const CELL_BOX = 'h-8 w-full px-2 rounded-md border text-sm leading-8';

const EditableCell = ({ value, placeholder, editable, type = 'text', onSave, label }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const start = () => {
    if (!editable) return;
    setDraft(value || '');
    setEditing(true);
  };

  const finish = async (save) => {
    setEditing(false);
    if (save && draft.trim() !== (value || '').trim()) await onSave(draft);
  };

  if (editing) {
    return (
      <input
        autoFocus
        type={type}
        aria-label={label}
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => finish(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          else if (e.key === 'Escape') finish(false);
        }}
        className={cn(CELL_BOX, 'block border-input bg-background outline-none focus-visible:ring-2 focus-visible:ring-ring')}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      disabled={!editable}
      aria-label={editable ? `Edit ${label}` : label}
      className={cn(
        CELL_BOX,
        'block text-left truncate border-transparent',
        editable ? 'cursor-text hover:bg-muted' : 'cursor-default',
        !value && 'text-muted-foreground'
      )}
    >
      {value || '-'}
    </button>
  );
};

/**
 * UsersTable - Displays and manages team members
 *
 * Reads the organisation's user_profiles rows directly (see
 * useUserCollaboration). Owners can edit names and phone numbers inline, drag
 * rows to reorder them, and resend a pending invitation. Roles are Appwrite
 * team membership roles, edited in UserRolesForm.
 */
const UsersTable = ({ isActive = true }) => {
  const [deactivateWarning, setDeactivateWarning] = useState(null);
  const [rolesFormOpen, setRolesFormOpen] = useState(false);
  const [selectedUserForRoles, setSelectedUserForRoles] = useState(null);
  const [resending, setResending] = useState(null);

  const {
    users,
    currentUser,
    canManage,
    loading,
    updateUser,
    reorderUsers,
    resendInvite,
    setMemberRoles
  } = useUserCollaboration(isActive);

  const save = async (user, field, value) => {
    try {
      await updateUser(user.id, field, value);
    } catch (error) {
      toast.error(`Failed to update: ${error.message}`);
    }
  };

  const handleReorder = async (activeId, overId) => {
    try {
      await reorderUsers(activeId, overId);
    } catch (error) {
      toast.error(`Could not save the new order: ${error.message}`);
    }
  };

  const handleResend = async (user) => {
    setResending(user.id);
    try {
      await resendInvite(user.id);
      toast.success(`Invitation sent again to ${user.email}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setResending(null);
    }
  };

  const handleConfirmDeactivate = async () => {
    try {
      await updateUser(deactivateWarning.id, 'status', false);
      toast.success('User deactivated successfully');
      setDeactivateWarning(null);
    } catch (error) {
      toast.error('Failed to deactivate user');
    }
  };

  const handleStatusChange = async (user, checked) => {
    if (!checked) {
      if (user.id === currentUser?.id) {
        toast.error('You cannot deactivate your own account');
        return;
      }
      setDeactivateWarning(user);
      return;
    }
    try {
      await updateUser(user.id, 'status', true);
      toast.success('User reactivated successfully');
    } catch (error) {
      toast.error('Failed to reactivate user');
    }
  };

  const header = (
    <div className="flex items-center border-b bg-muted px-3 py-2">
      <DragHandleSpacer />
      <div className={cn(GRID, 'flex-1 px-2 text-sm font-semibold')}>
        <div>First name</div>
        <div>Last name</div>
        <div>Email</div>
        <div>Phone</div>
        <div>Roles</div>
        <div>Active</div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="border rounded-lg overflow-x-auto">
        <div className={MIN_WIDTH}>
          {header}
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center border-t px-3 py-1.5">
              <DragHandleSpacer />
              <div className={cn(GRID, 'flex-1 px-2')}>
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-4" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center">
        <p className="text-muted-foreground">
          No {isActive ? 'active' : 'inactive'} users found
        </p>
      </div>
    );
  }

  const byId = new Map(users.map((u) => [u.id, u]));

  const renderCells = (userId) => {
    const user = byId.get(userId);
    if (!user) return null;
    const isSelf = user.id === currentUser?.id;
    const editable = canManage || isSelf;

    return (
      <div className={cn(GRID, 'py-1.5')}>
        <EditableCell
          label="first name"
          value={user.firstName}
          placeholder="First name"
          editable={editable}
          onSave={(v) => save(user, 'firstName', v)}
        />
        <EditableCell
          label="last name"
          value={user.lastName}
          placeholder="Last name"
          editable={editable}
          onSave={(v) => save(user, 'lastName', v)}
        />

        {/* Email (read-only), with invitation state */}
        <div className="flex h-8 min-w-0 items-center gap-2 px-2 text-sm text-muted-foreground">
          <span className="truncate">{user.email}</span>
          {user.invited && (
            <>
              <Badge variant="outline" className="shrink-0 text-xs">Invited</Badge>
              {canManage && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0"
                  title="Resend invitation"
                  aria-label={`Resend invitation to ${user.email}`}
                  disabled={resending === user.id}
                  onClick={() => handleResend(user)}
                >
                  <Mail className="h-3.5 w-3.5" />
                </Button>
              )}
            </>
          )}
        </div>

        <EditableCell
          label="phone"
          type="tel"
          value={user.phone}
          placeholder="Phone number"
          editable={editable}
          onSave={(v) => save(user, 'phone', v)}
        />

        {/* Roles from membership */}
        <button
          type="button"
          className="flex h-8 min-w-0 items-center gap-1 overflow-hidden rounded-md px-2 hover:bg-muted"
          onClick={() => {
            setSelectedUserForRoles(user);
            setRolesFormOpen(true);
          }}
        >
          {user.teamRoles.length > 0 ? (
            user.teamRoles.map((role) => (
              <Badge key={role} variant="secondary" className="shrink-0 text-xs capitalize">
                {role}
              </Badge>
            ))
          ) : (
            <span className="text-sm text-muted-foreground">No roles</span>
          )}
        </button>

        <div className="flex h-8 items-center px-2">
          <Checkbox
            checked={user.status}
            disabled={!canManage || isSelf}
            aria-label={`${user.email} is active`}
            onCheckedChange={(checked) => handleStatusChange(user, checked)}
          />
        </div>
      </div>
    );
  };

  const ids = users.map((u) => u.id);

  return (
    <>
      <div className="border rounded-lg overflow-x-auto">
        <div className={MIN_WIDTH}>
          {header}
          {canManage ? (
            <SortableList
              ids={ids}
              onReorder={handleReorder}
              renderRow={renderCells}
              className="space-y-0 divide-y px-3"
              rowClassName="hover:bg-muted/50"
            />
          ) : (
            <div className="divide-y px-3">
              {ids.map((id) => (
                <div key={id} className="flex items-center hover:bg-muted/50">
                  <DragHandleSpacer />
                  <div className="flex-1 min-w-0">{renderCells(id)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Deactivation warning dialog */}
      {deactivateWarning && (
        <Dialog open={!!deactivateWarning} onOpenChange={() => setDeactivateWarning(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Deactivate User</DialogTitle>
              <DialogDescription>
                Are you sure you want to deactivate <strong>{deactivateWarning.email}</strong>? This user will no longer have access to the system.
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setDeactivateWarning(null)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleConfirmDeactivate}>
                Deactivate
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* User roles edit dialog */}
      <UserRolesForm
        isOpen={rolesFormOpen}
        onClose={() => {
          setRolesFormOpen(false);
          setSelectedUserForRoles(null);
        }}
        user={selectedUserForRoles}
        onRolesUpdated={(updated) => setMemberRoles(updated.id, updated.teamRoles)}
      />
    </>
  );
};

export default UsersTable;
